import { SupportedLocales } from '@allesauto/utils/consts';
import { Calendar16 } from '@carbon/icons-react';
import clsx from 'clsx';
import de from 'date-fns/locale/de';
import en_GB from 'date-fns/locale/en-GB';
import en_US from 'date-fns/locale/en-US';
import React, { forwardRef, useEffect, useState } from 'react';
import reactDatePicker, {
  ReactDatePickerProps,
  registerLocale,
} from 'react-datepicker';

import LoaderOverlay from '../loaderOverlay';

const ReactDatePicker: any =
  'default' in reactDatePicker
    ? (reactDatePicker as any).default // default export not recognized in next.js
    : reactDatePicker;

import './DatePicker.css';

export interface DatePickerProps extends ReactDatePickerProps {
  darkBg?: boolean;
  defaulSelectedtDate?: Date | null;
  locale: SupportedLocales;
  minDate?: Date;
  errorState?: boolean;
  errorMessage?: string;
  showYearDropdown?: boolean;
  maxDate?: Date;
  dateFormat?: string;
  trackingId?: string;
  allowWeekends?: boolean;
  disablePublicHolidays?: boolean;
  blockedWeekends?: boolean;
  isLoading?: boolean;
}

const DateFnsLocales = {
  de: de,
  en: en_GB,
  'de-DE': de,
  'en-US': en_US,
  'en-GB': en_GB,
};

const DatePicker = (props: DatePickerProps) => {
  const {
    darkBg = false,
    disabled = false,
    placeholderText,
    value,
    onChange,
    locale,
    defaulSelectedtDate = null,
    minDate = new Date(),
    errorState = false,
    errorMessage,
    showYearDropdown = false,
    maxDate,
    dateFormat = 'dd.MM.yyyy',
    trackingId,
    allowWeekends = false,
    disablePublicHolidays = false,
    blockedWeekends = false,
    includeDates,
    onMonthChange,
    isLoading = false,
    forceShowMonthNavigation = false,
  } = props;

  registerLocale(locale, DateFnsLocales[locale]);

  const [selectedDate, setSelectedDate] = useState<Date | null>(
    defaulSelectedtDate
  );

  const isWeekday = (date: Date) => {
    const day = date.getDay();
    const dateOptions = { day: '2-digit', month: '2-digit' } as const;
    const holiday = date.toLocaleDateString('en-GB', dateOptions);

    // Germany National Public Holidays
    const holidays = [
      '01/01',
      '29/03',
      '01/04',
      '01/05',
      '09/05',
      '20/05',
      '03/10',
      '25/12',
      '26/12',
    ];

    const isHoliday = holidays.includes(holiday);

    if (disablePublicHolidays) {
      return !isHoliday;
    }
    if (allowWeekends) {
      return true;
    }
    if (blockedWeekends) {
      return day !== 0 && day !== 6;
    }

    return day !== 0 && !isHoliday;
  };

  useEffect(() => {
    setSelectedDate(defaulSelectedtDate);
  }, [defaulSelectedtDate]);

  const yearsToDisplay = 100;

  const className = {
    container: 'relative',
    inputContainer: clsx(
      'flex h-[50px] items-center min-w-0 pl-20px pr-30px py-10px relative ring-1 rounded-default w-full focus-within:ring-[3px] hover:ring-[3px]',
      darkBg && 'ring-white ',
      !darkBg && 'ring-purple80 monochrome:ring-black ',
      disabled && !darkBg && 'ring-grey50 focus-within:ring-1 hover:ring-1',
      disabled &&
        darkBg &&
        'ring-purple100 monochrome:ring-black focus-within:ring-1 hover:ring-1',
      errorState && '!ring-red60 !ring-[3px]'
    ),
    inputField: clsx(
      'bg-transparent border-0 font-weight-bold max-w-full min-h-full min-w-0 outline-none placeholder:font-weight-normal',
      darkBg &&
        'text-white focus:placeholder-lightPurple monochrome:focus:placeholder-grey60 placeholder-transparent flex items-center',
      !darkBg &&
        'flex items-center text-purple80 placeholder-purple80 focus:placeholder-lightPurple monochrome:text-black monochrome:placeholder-black monochrome:focus:placeholder-grey60',

      disabled && !darkBg && 'placeholder-grey50',
      disabled &&
        darkBg &&
        'placeholder-purple100 monochrome:placeholder-black',
      errorState && '!text-red60 !focus:placeholder-red60 !placeholder-red60'
    ),
    placeholder: clsx(
      'text-purple80 monochrome:text-grey60',
      disabled && !darkBg && 'text-grey50',
      disabled && darkBg && 'text-purple100 monochrome:text-black',
      errorState && '!text-red60'
    ),
    calendarIcon: clsx(
      'ml-auto',
      darkBg &&
        'placeholder-green60 focus:placeholder-lightPurple text-white monochrome:placeholder-grey10 monochrome:focus:placeholder-grey60 flex items-center',
      !darkBg &&
        'text-purple80 focus:placeholder-lightPurple monochrome:text-black monochrome:focus:placeholder-grey60 placeholder-transparent',
      disabled && !darkBg && 'text-grey50',
      disabled && darkBg && 'text-purple100 monochrome:text-black',
      errorState && '!text-red60'
    ),
    datePickerError: clsx(
      '-bottom-20px absolute left-0 text-p3 text-red60 w-full'
    ),
    loaderWrapper: 'absolute',
    loading:
      'absolute h-full w-full z-50 flex justify-center items-center !top-auto !left-auto',
  };

  const onChangeHandler = (
    date: Date | null,
    event: React.SyntheticEvent<any> | undefined
  ) => {
    onChange && onChange(date, event);
    setSelectedDate(date);
  };

  const CustomInputCalendarIcon = forwardRef<HTMLDivElement>(
    (props: any, ref) => (
      <div
        ref={ref}
        className={className.inputContainer}
        onClick={props.onClick}
      >
        {!props.value && placeholderText && (
          <span className={className.placeholder}>{placeholderText}</span>
        )}
        <span className={className.inputField}>{props.value}</span>
        <Calendar16 className={className.calendarIcon} />
      </div>
    )
  );
  CustomInputCalendarIcon.displayName = 'datePickerWithIcon';

  return (
    <div
      className={className.container}
      data-tracking-id={`${trackingId}--date`}
    >
      <ReactDatePicker
        wrapperClassName={darkBg ? 'dark-bg' : ''}
        disabled={disabled}
        calendarClassName={darkBg ? 'dark-bg' : ''}
        value={value}
        selected={selectedDate}
        onChange={onChangeHandler}
        isClearable
        customInput={<CustomInputCalendarIcon />}
        locale={locale}
        minDate={minDate}
        showYearDropdown={showYearDropdown}
        yearDropdownItemNumber={showYearDropdown ? yearsToDisplay : undefined}
        scrollableYearDropdown={showYearDropdown}
        maxDate={maxDate}
        dateFormat={dateFormat}
        filterDate={isWeekday}
        onMonthChange={onMonthChange}
        includeDates={includeDates}
        forceShowMonthNavigation={forceShowMonthNavigation}
      >
        {isLoading && (
          <LoaderOverlay
            customClassName={{
              containerCustomClass: className.loaderWrapper,
              loaderCustomClass: className.loading,
            }}
          />
        )}
      </ReactDatePicker>
      {errorMessage && (
        <span className={className.datePickerError}>{errorMessage}</span>
      )}
    </div>
  );
};

export default DatePicker;
